(() => {
  // Safe when the section asset is emitted more than once on the same page.
  if (customElements.get('ks-media-slider')) return;
class KsMediaSlider extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.slides = [];
    this.currentIndex = 0;
    this.touchStart = null;
    this.abortController = null;
    this.iframeObserver = null;
    this.sectionObserver = null;
    this.inViewport = false;
    this.mobileMedia = window.matchMedia('(max-width: 749px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.interactionTimers = new Map();
    this.badgeShineTimers = new Map();
    this.buttonInteractionTimers = new Map();
    this.autoplayTimer = null;
    this.autoplayInView = false;
    this.desktopMedia = window.matchMedia('(min-width: 990px)');
  }

  connectedCallback() {
    this.track = this.querySelector('[data-ks-ms-track]');
    this.slides = Array.from(this.querySelectorAll('[data-ks-ms-slide]'));
    if (!this.track || !this.slides.length) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.classList.add('is-enhanced');

    this.setupNavigation();
    this.setupCardInteractions();
    this.setupButtonEnhancements();
    this.setupEntryReveal();
    this.setupThemeEditor();
    this.lazyLoadIframes();
    this.setupVideoVisibility();
    this.setupAutoplay();
    this.syncResponsiveState(true);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.iframeObserver?.disconnect();
    this.iframeObserver = null;
    this.sectionObserver?.disconnect();
    this.sectionObserver = null;
    this.interactionTimers.forEach((timer) => window.clearTimeout(timer));
    this.interactionTimers.clear();
    this.badgeShineTimers.forEach((timer) => window.clearTimeout(timer));
    this.badgeShineTimers.clear();
    this.buttonInteractionTimers.forEach((timer) => window.clearTimeout(timer));
    this.buttonInteractionTimers.clear();
    this.stopAutoplay();
    this.querySelectorAll('video').forEach((video) => video.pause());
  }

  get loopEnabled() {
    return this.dataset.mobileLoop === 'true';
  }

  get transitionDuration() {
    const parsed = Number.parseInt(this.dataset.transitionDuration || '700', 10);
    return Number.isFinite(parsed) ? Math.min(1200, Math.max(200, parsed)) : 700;
  }

  setupNavigation() {
    const signal = this.abortController.signal;
    const prev = this.querySelector('[data-ks-ms-prev]');
    const next = this.querySelector('[data-ks-ms-next]');

    prev?.addEventListener('click', () => this.move(-1), { signal });
    next?.addEventListener('click', () => this.move(1), { signal });

    this.track.addEventListener(
      'touchstart',
      (event) => {
        if (!this.mobileMedia.matches || event.touches.length !== 1) return;
        const touch = event.touches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY };
      },
      { passive: true, signal }
    );

    this.track.addEventListener(
      'touchend',
      (event) => {
        if (!this.mobileMedia.matches || !this.touchStart || !event.changedTouches.length) return;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStart.x;
        const deltaY = touch.clientY - this.touchStart.y;
        this.touchStart = null;

        const horizontal = Math.abs(deltaX) >= 42 && Math.abs(deltaX) > Math.abs(deltaY) * 1.08;
        if (!horizontal) {
          const slide = event.target?.closest?.('[data-ks-ms-slide]');
          if (slide) this.runTouchInteraction(slide);
          return;
        }
        this.move(deltaX < 0 ? 1 : -1);
      },
      { passive: true, signal }
    );

    this.track.addEventListener(
      'touchcancel',
      () => {
        this.touchStart = null;
      },
      { passive: true, signal }
    );

    this.mobileMedia.addEventListener?.('change', () => this.syncResponsiveState(true), { signal });
    window.addEventListener('resize', () => this.syncResponsiveState(false), { passive: true, signal });
    document.addEventListener('visibilitychange', () => { this.syncVideoPlayback(); this.syncAutoplay(); }, { signal });
  }

  setupCardInteractions() {
    const signal = this.abortController.signal;

    this.slides.forEach((slide) => {
      // Desktop: one badge shine for each real mouse entry over the card.
      // It is JS-driven so it does not depend on hover media-query quirks.
      slide.addEventListener(
        'mouseenter',
        () => {
          if (!this.desktopMedia.matches) return;
          this.runBadgeShine(slide);
        },
        { signal }
      );

      // Tablet/touch keeps the existing tactile card feedback + badge shine.
      slide.addEventListener(
        'pointerup',
        (event) => {
          if (event.pointerType === 'mouse' || this.mobileMedia.matches) return;
          this.runTouchInteraction(slide);
        },
        { passive: true, signal }
      );
    });
  }

  runBadgeShine(slide) {
    if (!slide) return;
    const previous = this.badgeShineTimers.get(slide);
    if (previous) window.clearTimeout(previous);
    slide.classList.remove('is-hover-shine');
    void slide.offsetWidth;
    slide.classList.add('is-hover-shine');
    const timer = window.setTimeout(() => {
      slide.classList.remove('is-hover-shine');
      this.badgeShineTimers.delete(slide);
    }, 760);
    this.badgeShineTimers.set(slide, timer);
  }

  setupButtonEnhancements() {
    const signal = this.abortController.signal;
    const icon = this.dataset.buttonIcon || 'none';
    const iconPosition = this.dataset.buttonIconPosition || 'right';
    const buttons = Array.from(this.querySelectorAll('.ks-section-content-block .button'));

    buttons.forEach((button) => {
      if (icon !== 'none' && !button.querySelector('.ks-media-slider__button-icon')) {
        const iconNode = this.createButtonIcon(icon);
        if (iconNode) {
          iconNode.classList.add('ks-media-slider__button-icon');
          if (iconPosition === 'left') button.prepend(iconNode);
          else button.append(iconNode);
        }
      }

      button.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'mouse') return;
          this.runButtonInteraction(button);
        },
        { passive: true, signal }
      );

      button.addEventListener(
        'focusin',
        () => this.runButtonInteraction(button),
        { signal }
      );
    });
  }

  createButtonIcon(name) {
    const paths = {
      'arrow-right': '<path d="M5 12h14M14 7l5 5-5 5"/>',
      sparkles: '<path d="M12 2.5c.5 3.2 2.3 5 5.5 5.5-3.2.5-5 2.3-5.5 5.5-.5-3.2-2.3-5-5.5-5.5 3.2-.5 5-2.3 5.5-5.5ZM18.5 13.5c.3 2 1.5 3.2 3.5 3.5-2 .3-3.2 1.5-3.5 3.5-.3-2-1.5-3.2-3.5-3.5 2-.3 3.2-1.5 3.5-3.5Z"/>',
      check: '<path d="m5 12 4 4 10-10"/>',
      bolt: '<path d="M13 2 5.8 13h5.1L10 22l8.2-12h-5.1L13 2Z"/>',
    };
    if (!paths[name]) return null;
    const wrapper = document.createElement('span');
    wrapper.innerHTML = `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">${paths[name]}</svg>`;
    return wrapper;
  }

  runButtonInteraction(button) {
    if (!button || this.dataset.buttonHover === 'none') return;
    const previous = this.buttonInteractionTimers.get(button);
    if (previous) window.clearTimeout(previous);
    button.classList.remove('is-button-interacting');
    void button.offsetWidth;
    button.classList.add('is-button-interacting');
    const timer = window.setTimeout(() => {
      button.classList.remove('is-button-interacting');
      this.buttonInteractionTimers.delete(button);
    }, 760);
    this.buttonInteractionTimers.set(button, timer);
  }

  runTouchInteraction(slide) {
    if (!slide || this.reducedMotion.matches || this.dataset.tapInteraction === 'none') return;
    const previous = this.interactionTimers.get(slide);
    if (previous) window.clearTimeout(previous);
    slide.classList.remove('is-touching');
    void slide.offsetWidth;
    slide.classList.add('is-touching');
    const timer = window.setTimeout(() => {
      slide.classList.remove('is-touching');
      this.interactionTimers.delete(slide);
    }, 520);
    this.interactionTimers.set(slide, timer);
  }

  move(delta, fromAutoplay = false) {
    if (!this.mobileMedia.matches || this.slides.length < 2) return;
    let nextIndex = this.currentIndex + delta;

    if (this.loopEnabled || fromAutoplay) {
      nextIndex = (nextIndex + this.slides.length) % this.slides.length;
    } else {
      nextIndex = Math.max(0, Math.min(this.slides.length - 1, nextIndex));
    }

    this.goTo(nextIndex, true);
    if (!fromAutoplay) this.syncAutoplay();
  }

  goTo(index, animate = true) {
    const safeIndex = Math.max(0, Math.min(this.slides.length - 1, Number(index) || 0));
    this.currentIndex = safeIndex;

    if (this.mobileMedia.matches) {
      this.track.style.transitionDuration = this.reducedMotion.matches || !animate ? '1ms' : `${this.transitionDuration}ms`;
      this.track.style.transform = `translate3d(-${safeIndex * 100}%, 0, 0)`;
    } else {
      this.track.style.removeProperty('transform');
      this.track.style.removeProperty('transition-duration');
    }

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === safeIndex;
      slide.classList.toggle('is-active', active);
      if (this.mobileMedia.matches) slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      else slide.removeAttribute('aria-hidden');
    });

    this.updatePagination();
    this.syncVideoPlayback();
    if (animate && this.mobileMedia.matches) this.animateActiveCard();
  }

  animateActiveCard() {
    if (this.reducedMotion.matches || this.dataset.mobileTransition === 'slide') return;
    const slide = this.slides[this.currentIndex];
    if (!slide) return;

    slide.classList.remove('is-entering');
    void slide.offsetWidth;
    slide.classList.add('is-entering');
    window.setTimeout(() => slide.classList.remove('is-entering'), this.transitionDuration + 80);
  }

  updatePagination() {
    const current = this.querySelector('[data-ks-ms-current]');
    const progress = this.querySelector('[data-ks-ms-progress]');
    const live = this.querySelector('[data-ks-ms-live]');
    const display = String(this.currentIndex + 1).padStart(2, '0');

    if (current) current.textContent = display;
    if (progress) progress.style.width = `${((this.currentIndex + 1) / this.slides.length) * 100}%`;
    if (live) live.textContent = `Proyecto ${this.currentIndex + 1} de ${this.slides.length}`;
  }

  syncResponsiveState(animate = false) {
    if (!this.mobileMedia.matches) this.currentIndex = 0;
    this.goTo(this.currentIndex, animate && this.mobileMedia.matches);
    this.syncAutoplay();
  }

  setupEntryReveal() {
    const entryType = this.dataset.entryAnimation || 'none';
    const reveal = () => {
      if (this.classList.contains('is-visible')) return;
      requestAnimationFrame(() => this.classList.add('is-visible'));
    };

    if (entryType === 'none') {
      this.classList.add('is-visible');
      return;
    }

    this.classList.remove('is-visible');

    if (!('IntersectionObserver' in window)) {
      requestAnimationFrame(() => requestAnimationFrame(reveal));
      return;
    }

    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.inViewport = entry.isIntersecting;
        this.autoplayInView = entry.isIntersecting;
        if (entry.isIntersecting) reveal();
        this.syncVideoPlayback();
        this.syncAutoplay();
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
    );

    // Two frames guarantee the hidden/armed state is painted first. This is
    // especially important on wide desktop viewports where the observer can
    // otherwise resolve before the first composited frame and skip the motion.
    requestAnimationFrame(() => requestAnimationFrame(() => this.sectionObserver?.observe(this)));
  }

  setupThemeEditor() {
    const signal = this.abortController.signal;

    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const slide = event.target?.closest?.('[data-ks-ms-slide]');
        if (!slide) return;
        const index = this.slides.indexOf(slide);
        if (index < 0) return;

        if (this.mobileMedia.matches) this.goTo(index, true);
        else slide.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest' });
      },
      { signal }
    );

    this.addEventListener(
      'shopify:section:select',
      () => {
        this.classList.remove('is-visible');
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-visible')));
      },
      { signal }
    );
  }

  setupVideoVisibility() {
    if (this.sectionObserver || !('IntersectionObserver' in window)) return;
    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        this.inViewport = entry.isIntersecting;
        this.autoplayInView = entry.isIntersecting;
        this.syncVideoPlayback();
        this.syncAutoplay();
      },
      { threshold: 0.08 }
    );
    this.sectionObserver.observe(this);
  }

  get autoplayMobileEnabled() {
    return this.dataset.autoplayMobile === 'true';
  }

  get autoplaySpeed() {
    const parsed = Number.parseInt(this.dataset.autoplaySpeed || '6000', 10);
    return Number.isFinite(parsed) ? Math.min(12000, Math.max(3000, parsed)) : 6000;
  }

  setupAutoplay() {
    if (!('IntersectionObserver' in window)) {
      this.autoplayInView = true;
      this.inViewport = true;
    }
    this.syncAutoplay();
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  syncAutoplay() {
    this.stopAutoplay();
    if (
      !this.autoplayMobileEnabled ||
      !this.mobileMedia.matches ||
      this.slides.length < 2 ||
      !this.autoplayInView ||
      document.hidden
    ) return;

    this.autoplayTimer = window.setTimeout(() => {
      this.autoplayTimer = null;
      if (!this.mobileMedia.matches || document.hidden || !this.autoplayInView) {
        this.syncAutoplay();
        return;
      }
      this.move(1, true);
      this.syncAutoplay();
    }, this.autoplaySpeed);
  }

  syncVideoPlayback() {
    this.slides.forEach((slide, index) => {
      const video = slide.querySelector('video[data-autoplay="true"]');
      if (!video) return;

      const activeForDevice = this.inViewport && (!this.mobileMedia.matches || index === this.currentIndex);
      const canPlay = activeForDevice && !document.hidden;
      if (canPlay) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  lazyLoadIframes() {
    const iframes = Array.from(this.querySelectorAll('iframe[data-src]'));
    if (!iframes.length) return;

    const loadIframe = (iframe) => {
      if (!iframe.dataset.src) return;
      iframe.src = iframe.dataset.src;
      iframe.removeAttribute('data-src');
    };

    if (!('IntersectionObserver' in window)) {
      iframes.forEach(loadIframe);
      return;
    }

    this.iframeObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadIframe(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '320px 0px' }
    );

    iframes.forEach((iframe) => this.iframeObserver.observe(iframe));
  }
}

if (!customElements.get('ks-media-slider')) {
  customElements.define('ks-media-slider', KsMediaSlider);
}

})();
